import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import FooterCTA from "../components/FooterCTA";
import useDocumentMeta from "../lib/useDocumentMeta";
import { CONSULT_LINK } from "../lib/constants";
import { useCollection } from "../lib/useCollection";
import { resolveMediaUrl } from "../lib/mediaUrl";

const CATEGORIES = ["All", "Packaging", "Branding", "Signage", "Logo", "Social Media"];

// Helper: stable picsum image with category-themed seed.
const ps = (seed, h) => `https://picsum.photos/seed/${encodeURIComponent(seed)}/600/${h}`;

// 24 items — varied heights for organic masonry, distributed across categories.
const ITEMS = [
    // Row 1
    { id: "p01", category: "Packaging", src: ps("rg-pack-01", 720) },
    { id: "p02", category: "Branding",  src: ps("rg-brand-01", 480) },
    { id: "p03", category: "Logo",      src: ps("rg-logo-01", 600) },
    { id: "p04", category: "Signage",   src: ps("rg-sign-01", 520) },
    { id: "p05", category: "Social Media", src: ps("rg-social-01", 720) },
    { id: "p06", category: "Packaging", src: "https://images.unsplash.com/photo-1677753669144-66e809db63e3?auto=format&fit=crop&w=600&q=70" },

    // Row 2
    { id: "p07", category: "Branding",  src: "https://images.unsplash.com/photo-1707214520187-45fc42b82bdc?auto=format&fit=crop&w=600&q=70" },
    { id: "p08", category: "Logo",      src: ps("rg-logo-02", 460) },
    { id: "p09", category: "Signage",   src: "https://images.unsplash.com/photo-1557044829-d613e848004a?auto=format&fit=crop&w=600&q=70" },
    { id: "p10", category: "Packaging", src: ps("rg-pack-03", 540) },
    { id: "p11", category: "Branding",  src: ps("rg-brand-03", 700) },
    { id: "p12", category: "Social Media", src: ps("rg-social-02", 440) },

    // Row 3
    { id: "p13", category: "Logo",      src: ps("rg-logo-03", 620) },
    { id: "p14", category: "Packaging", src: ps("rg-pack-04", 480) },
    { id: "p15", category: "Signage",   src: ps("rg-sign-02", 540) },
    { id: "p16", category: "Branding",  src: ps("rg-brand-06", 460) },
    { id: "p17", category: "Social Media", src: ps("rg-social-03", 720) },
    { id: "p18", category: "Logo",      src: ps("rg-logo-04", 380) },

    // Row 4 — extra items revealed via Load More
    { id: "p19", category: "Packaging", src: ps("rg-pack-05", 660) },
    { id: "p20", category: "Branding",  src: ps("rg-brand-07", 580) },
    { id: "p21", category: "Signage",   src: ps("rg-sign-03", 600) },
    { id: "p22", category: "Social Media", src: ps("rg-social-04", 520) },
    { id: "p23", category: "Logo",      src: ps("rg-logo-05", 560) },
    { id: "p24", category: "Packaging", src: ps("rg-pack-06", 700) },
];

const INITIAL_VISIBLE = 18;
const LOAD_STEP = 6;

const PortfolioPage = () => {
    useDocumentMeta({
        title: "Our Work — Branding, Packaging & Signage Portfolio | Raj Graphics Nagpur",
        description:
            "Selected packaging, branding, signage, logo and social media projects from Raj Graphics — a design and printing studio in Nagpur.",
        canonical: "/portfolio",
    });

    const backendItems = useCollection("/portfolio", []);
    const allItems = useMemo(
        () =>
            backendItems.length
                ? backendItems.map((p) => ({
                      id: p.id,
                      category: p.category,
                      src: resolveMediaUrl(p.image_url),
                      title: p.title,
                      linked: true,
                  }))
                : ITEMS,
        [backendItems],
    );

    const [active, setActive] = useState("All");
    const [visible, setVisible] = useState(INITIAL_VISIBLE);

    const filtered = useMemo(() => {
        if (active === "All") return allItems;
        return allItems.filter((i) => i.category === active);
    }, [active, allItems]);

    const shown = filtered.slice(0, visible);
    const canLoadMore = visible < filtered.length;

    // Reset visible count when filter changes
    useEffect(() => {
        setVisible(INITIAL_VISIBLE);
    }, [active]);

    return (
        <main data-testid="portfolio-page" className="bg-[#050505]">
            {/* Header */}
            <section className="relative pt-36 pb-10 md:pt-44 md:pb-14 overflow-hidden">
                <div className="absolute inset-0 grid-pattern radial-fade opacity-50 pointer-events-none" />
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-gold/[0.06] blur-[140px] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-5">
                        Portfolio
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="font-poppins font-bold tracking-tight text-white text-5xl sm:text-6xl lg:text-8xl leading-[1.02]"
                        data-testid="portfolio-headline"
                    >
                        Our <span className="gold-gradient-text">Work</span>.
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mt-5 font-inter text-base sm:text-lg text-zinc-400 max-w-xl leading-relaxed"
                        data-testid="portfolio-subtitle"
                    >
                        Real projects. Real results.
                    </motion.p>

                    {/* Filters */}
                    <div
                        data-testid="portfolio-filters"
                        className="mt-12 flex flex-wrap gap-2 md:gap-3"
                    >
                        {CATEGORIES.map((c) => {
                            const slug = c.toLowerCase().replace(/\s+/g, "-");
                            const isActive = active === c;
                            return (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setActive(c)}
                                    aria-pressed={isActive}
                                    data-testid={`portfolio-filter-${slug}`}
                                    className={`font-inter text-sm px-4 py-2 rounded-full border transition-colors ${
                                        isActive
                                            ? "bg-gold text-black border-gold"
                                            : "border-white/10 text-zinc-300 hover:border-gold hover:text-gold"
                                    }`}
                                >
                                    {c}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Edge‑to‑edge masonry */}
            <section className="relative" data-testid="portfolio-grid-section">
                <div
                    data-testid="portfolio-grid"
                    className="columns-2 md:columns-3 lg:columns-6 gap-[2px] [column-fill:_balance]"
                >
                    <AnimatePresence>
                        {shown.map((item) => (
                            <motion.figure
                                key={item.id}
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.97 }}
                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                data-testid={`portfolio-item-${item.id}`}
                                data-category={item.category}
                                className="relative block w-full mb-[2px] break-inside-avoid overflow-hidden bg-[#0a0a0a] group"
                            >
                                <Link
                                    to={item.linked ? `/portfolio/${item.id}` : "/portfolio"}
                                    onClick={item.linked ? undefined : (e) => e.preventDefault()}
                                    className="block cursor-pointer"
                                    data-testid={`portfolio-link-${item.id}`}
                                >
                                    <img
                                        src={item.src}
                                        alt={`${item.title || item.category} — ${item.category} project by Raj Graphics`}
                                        loading="lazy"
                                        decoding="async"
                                        className="block w-full h-auto transition-transform duration-700 ease-out group-hover:scale-105"
                                    />
                                    <div className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/55 transition-colors duration-300 flex items-end p-4 md:p-5">
                                        <span
                                            className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 font-inter text-[10px] md:text-xs uppercase tracking-[0.25em] text-gold"
                                            data-testid={`portfolio-tag-${item.id}`}
                                        >
                                            {item.title ? `${item.title} · ${item.category}` : item.category}
                                        </span>
                                    </div>
                                </Link>
                            </motion.figure>
                        ))}
                    </AnimatePresence>
                </div>

                {/* Empty state */}
                {filtered.length === 0 && (
                    <div className="px-6 md:px-12 py-24 text-center font-inter text-zinc-500">
                        No work in this category yet — more coming soon.
                    </div>
                )}
            </section>

            {/* Load more + CTA */}
            <section className="relative py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col items-center gap-12 text-center">
                    {canLoadMore && (
                        <button
                            type="button"
                            onClick={() => setVisible((v) => v + LOAD_STEP)}
                            data-testid="portfolio-load-more"
                            className="group inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-inter font-medium px-8 py-3.5 rounded-full hover:bg-gold/5 transition-colors"
                        >
                            <Plus
                                size={16}
                                className="transition-transform group-hover:rotate-90 duration-300"
                            />
                            Load More
                        </button>
                    )}

                    <div className="max-w-2xl">
                        <h2
                            className="font-poppins font-semibold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight"
                            data-testid="portfolio-bottom-cta-headline"
                        >
                            Want results like these?
                        </h2>
                        <a
                            href={CONSULT_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="portfolio-bottom-cta"
                            className="mt-7 inline-flex items-center gap-2 font-poppins font-medium text-gold text-lg sm:text-xl hover:underline underline-offset-4 decoration-gold/50"
                        >
                            Let's talk
                            <ArrowRight
                                size={20}
                                className="transition-transform group-hover:translate-x-1"
                            />
                        </a>
                    </div>
                </div>
            </section>

            <FooterCTA />
        </main>
    );
};

export default PortfolioPage;
